import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  ShieldCheck,
  Users as UsersIcon,
  XCircle,
} from "lucide-react";
import { api, ApiError } from "../../api";
import { Button, Card, PageHeader, cx } from "../../ui";

/**
 * Central verification queue for Property Management / Admin.
 *
 * Two surfaces, both scoped to the principal's managed buildings:
 *
 *   1. Pending owner liability policies — Pass / Reject inline.
 *      The owner uploads at /my/insurance; until a PM marks the row
 *      passed, it doesn't satisfy the project-submission gate, so
 *      this is the action that unblocks Cat B/C submissions.
 *
 *   2. Contractor compliance roll-up — every active contractor on
 *      every open project, with COI tally + last-document
 *      timestamp. Acts as an at-a-glance triage view; the
 *      structured per-coverage-type COI entry happens on the
 *      project Insurance panel (the deep-link).
 */

type OwnerPolicy = {
  id: string;
  unitId: string;
  unitNumber: string;
  buildingId: string;
  ownerUserId: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  carrierName: string;
  policyNumber: string;
  perIncidentLimitCents: string;
  effectiveDate: string;
  expirationDate: string;
  validationStatus: string;
  validatedAt: string | null;
  documentId: string | null;
  createdAt: string;
};

type ContractorComplianceRow = {
  projectId: string;
  projectTitle: string;
  unitNumber: string;
  statusCode: string;
  companyId: string;
  companyName: string;
  discipline: string;
  role: string;
  documentCount: number;
  latestDocumentAt: string | null;
  coiPassed: number;
  coiPending: number;
  coiFailed: number;
};

type Queue = {
  ownerPolicies: OwnerPolicy[];
  contractorCompliance: ContractorComplianceRow[];
};

export function AdminInsuranceReview() {
  const [queue, setQueue] = useState<Queue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = async () => {
    setError(null);
    try {
      const r = await api.get<{ data: Queue }>(
        "/api/management/insurance/queue",
      );
      setQueue(r.data);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `Load failed (${err.status})`
          : String(err),
      );
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const validate = async (
    policy: OwnerPolicy,
    decision: "passed" | "failed",
  ) => {
    if (
      decision === "failed" &&
      typeof window !== "undefined" &&
      !window.confirm(
        `Reject ${policy.carrierName} · ${policy.policyNumber} for ${policy.ownerFirstName} ${policy.ownerLastName}? The owner will need to re-upload.`,
      )
    ) {
      return;
    }
    setBusyId(policy.id);
    try {
      await api.post(
        `/api/management/liability-policies/${policy.id}/validate`,
        { decision },
      );
      await reload();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `Validate failed (${err.status})`
          : String(err),
      );
    } finally {
      setBusyId(null);
    }
  };

  const pendingPolicies =
    queue?.ownerPolicies.filter((p) => p.validationStatus === "pending") ??
    [];
  const otherPolicies =
    queue?.ownerPolicies.filter((p) => p.validationStatus !== "pending") ??
    [];

  return (
    <div>
      <Card>
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-2">
          <ShieldCheck size={18} /> Owner liability policies
          {pendingPolicies.length > 0 && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-status-review-bg text-status-review-fg">
              {pendingPolicies.length} pending
            </span>
          )}
        </h2>
        <p className="text-[12.5px] text-ink-500 mb-3">
          Owner-uploaded $500K personal liability certificates awaiting
          review. Approving a policy unblocks Cat B/C project submissions
          for that owner. Rejecting forces the owner to re-upload.
        </p>

        {error && <div className="alert-error mb-3">{error}</div>}

        {queue === null ? (
          <p className="text-ink-500 text-sm m-0">Loading…</p>
        ) : pendingPolicies.length === 0 && otherPolicies.length === 0 ? (
          <p className="text-ink-500 text-sm m-0">
            No owner liability policies on record yet.
          </p>
        ) : (
          <>
            {pendingPolicies.length > 0 && (
              <PolicyTable
                rows={pendingPolicies}
                busyId={busyId}
                onValidate={validate}
              />
            )}
            {otherPolicies.length > 0 && (
              <details className="mt-4">
                <summary className="cursor-pointer text-[13px] text-ink-600 hover:text-ink-800 mb-2">
                  Already validated ({otherPolicies.length})
                </summary>
                <PolicyTable
                  rows={otherPolicies}
                  busyId={busyId}
                  onValidate={validate}
                />
              </details>
            )}
          </>
        )}
      </Card>

      <Card>
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-2">
          <UsersIcon size={18} /> Contractor compliance
        </h2>
        <p className="text-[12.5px] text-ink-500 mb-3">
          Every contractor company currently on an open project, with their
          uploaded-document count and structured COI tally. Click into a
          project to record or update structured COI rows on its Insurance
          panel.
        </p>

        {queue === null ? (
          <p className="text-ink-500 text-sm m-0">Loading…</p>
        ) : queue.contractorCompliance.length === 0 ? (
          <p className="text-ink-500 text-sm m-0">
            No contractors are engaged on active projects.
          </p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Unit</th>
                  <th>Contractor</th>
                  <th>Discipline</th>
                  <th>Docs</th>
                  <th>COI</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {queue.contractorCompliance.map((c) => (
                  <tr key={`${c.projectId}-${c.companyId}-${c.role}`}>
                    <td>
                      <div className="text-[13px] font-medium text-ink-800 truncate max-w-[240px]">
                        {c.projectTitle}
                      </div>
                      <div className="text-[11px] text-ink-500">
                        {c.statusCode}
                      </div>
                    </td>
                    <td className="text-[12.5px] text-ink-600">
                      {c.unitNumber}
                    </td>
                    <td>
                      <div className="text-[13px] text-ink-800">
                        {c.companyName}
                      </div>
                    </td>
                    <td className="text-[12.5px] text-ink-600">
                      {c.role || c.discipline}
                    </td>
                    <td className="text-[12.5px] text-ink-700 tabular-nums">
                      {c.documentCount}
                      {c.latestDocumentAt && (
                        <div className="text-[10.5px] text-ink-400">
                          last{" "}
                          {new Date(c.latestDocumentAt).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td>
                      <CoiTally
                        passed={c.coiPassed}
                        pending={c.coiPending}
                        failed={c.coiFailed}
                      />
                    </td>
                    <td>
                      <Link
                        to={`/projects/${c.projectId}#section-insurance`}
                        className="inline-flex items-center gap-1 text-[12px] text-brand-700 hover:text-brand-800"
                      >
                        Open <ExternalLink size={12} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function PolicyTable({
  rows,
  busyId,
  onValidate,
}: {
  rows: OwnerPolicy[];
  busyId: string | null;
  onValidate: (
    policy: OwnerPolicy,
    decision: "passed" | "failed",
  ) => void | Promise<void>;
}) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Owner</th>
            <th>Unit</th>
            <th>Carrier</th>
            <th>Policy #</th>
            <th>Limit</th>
            <th>Effective</th>
            <th>Expires</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const limit = (
              Number(p.perIncidentLimitCents) / 100
            ).toLocaleString("en-US");
            return (
              <tr key={p.id}>
                <td>
                  <div className="text-[13px] font-medium text-ink-800">
                    {p.ownerFirstName} {p.ownerLastName}
                  </div>
                  <div className="text-[11px] text-ink-500">
                    {p.ownerEmail}
                  </div>
                </td>
                <td className="text-[12.5px] text-ink-600">{p.unitNumber}</td>
                <td className="text-[12.5px] text-ink-700">
                  {p.carrierName}
                </td>
                <td>
                  <code className="text-xs">{p.policyNumber}</code>
                </td>
                <td className="tabular-nums text-[12.5px] text-ink-700">
                  ${limit}
                </td>
                <td className="text-[11.5px] text-ink-600">
                  {p.effectiveDate}
                </td>
                <td className="text-[11.5px] text-ink-600">
                  {p.expirationDate}
                </td>
                <td>
                  <StatusBadge status={p.validationStatus} />
                </td>
                <td className="whitespace-nowrap">
                  {p.validationStatus === "pending" ? (
                    <div className="inline-flex gap-1">
                      <Button
                        size="sm"
                        variant="primary"
                        loading={busyId === p.id}
                        onClick={() => void onValidate(p, "passed")}
                      >
                        <CheckCircle2 size={13} /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={busyId === p.id}
                        onClick={() => void onValidate(p, "failed")}
                        className="text-status-rejected-fg hover:bg-status-rejected-bg"
                      >
                        <XCircle size={13} /> Reject
                      </Button>
                    </div>
                  ) : (
                    <Link
                      to={`/admin/users/${p.ownerUserId}/insurance`}
                      className="inline-flex items-center gap-1 text-[12px] text-brand-700 hover:text-brand-800"
                    >
                      Open <ExternalLink size={12} />
                    </Link>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cx(
        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider",
        status === "passed" &&
          "bg-status-approved-bg text-status-approved-fg",
        status === "failed" &&
          "bg-status-rejected-bg text-status-rejected-fg",
        status !== "passed" &&
          status !== "failed" &&
          "bg-status-review-bg text-status-review-fg",
      )}
    >
      {status}
    </span>
  );
}

function CoiTally({
  passed,
  pending,
  failed,
}: {
  passed: number;
  pending: number;
  failed: number;
}) {
  if (passed === 0 && pending === 0 && failed === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11.5px] text-ink-400">
        <AlertCircle size={12} /> none
      </span>
    );
  }
  return (
    <div className="inline-flex flex-wrap items-center gap-1">
      {passed > 0 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] bg-status-approved-bg text-status-approved-fg">
          <CheckCircle2 size={10} className="mr-0.5" />
          {passed}
        </span>
      )}
      {pending > 0 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] bg-status-review-bg text-status-review-fg">
          <ClipboardCheck size={10} className="mr-0.5" />
          {pending}
        </span>
      )}
      {failed > 0 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] bg-status-rejected-bg text-status-rejected-fg">
          <XCircle size={10} className="mr-0.5" />
          {failed}
        </span>
      )}
    </div>
  );
}
